/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import * as Stripe from '@stripe/stripe-js';
import { Auth } from 'aws-amplify';
import { AlertController, LoadingController } from '@ionic/angular';
import { AppService } from '../app.service';
import { getCurrencySymbol } from '@angular/common';
import { RequestError, StripeAddress } from '../app.types';

interface ProductInformation {
  id: string;
  name: string;
  description?: string;
  isLive: boolean;
}

@Component({
  templateUrl: './subscribe.page.html',
  styleUrls: ['./subscribe.page.scss'],
})
export class SubscribePage implements OnInit, OnDestroy {
  @ViewChild('couponInput') couponInput: HTMLIonInputElement;
  @ViewChild('cardNameElement') cardNameElement: HTMLIonInputElement;
  @ViewChild('permissionCheckbox') permissionCheckbox: HTMLIonCheckboxElement;

  get productInformation() {
    return this.appService.productInformation;
  }

  get priceInformation() {
    return this.appService.priceInformation;
  }

  get isLive() {
    return this.appService.isLive;
  }

  constructor(
    private router: Router,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private appService: AppService
  ) {}

  couponError: string;
  isCouponValidating = false;
  elements: Stripe.StripeElements;
  includeAddress = false;
  addressForm = {
    line1: '',
    line2: '',
    country: 'Australia',
    state: '',
    city: '',
    postal: '',
  };

  readonly COUNTRY_MAPPINGS = {
    Australia: 'AU',
    USA: 'US',
  };

  priceOption = this.appService.priceOption;
  priceAmount: string;
  paymentRequestAvailable = false;
  private paymentRequest: Stripe.PaymentRequest;
  loading: HTMLIonLoadingElement;
  errorDisplay: string;
  customerPermission = false;
  formReady = false;
  formReadyFields: {
    card: boolean;
    name: boolean;
    cvc: boolean;
    expiry: boolean;
  } = {
    card: false,
    name: false,
    cvc: false,
    expiry: false,
  };

  get paymentIntegratedEmail() {
    return this.appService.paymentIntegratedEmail;
  }

  get stripe() {
    return this.appService.getStripe();
  }

  get displayAmount() {
    return this.appService.priceInformation.unitAmount === 0
      ? 'Free'
      : `${getCurrencySymbol(
          this.appService.priceInformation.currency.toUpperCase(),
          'wide'
        )} ${((this.appService.priceInformation.unitAmount || 0) / 100).toFixed(
          2
        )}`;
  }

  get couponDisplayAmount() {
    return `${getCurrencySymbol(
      this.appService.priceInformation.currency,
      'wide'
    )} ${(this.appService.priceInformation.couponUnitAmount / 100).toFixed(
      2
    )} (discounted by ${this.appService.priceInformation.discount})`;
  }

  private couponValidationTimeout: NodeJS.Timeout;

  private getPaymentAmount(): number {
    return (
      this.appService.priceInformation.couponUnitAmount ??
      this.appService.priceInformation.unitAmount
    );
  }

  get actualPaymentAmount(): number {
    return this.getPaymentAmount();
  }

  get actualPaymentAmountDisplay(): string {
    const amount = this.actualPaymentAmount;
    if (amount === 0) {
      return 'Free';
    }
    return `${getCurrencySymbol(
      this.appService.priceInformation.currency.toUpperCase(),
      'wide'
    )} ${(amount / 100).toFixed(2)}`;
  }

  private updatePaymentRequest(): void {
    if (this.paymentRequest && this.paymentRequestAvailable) {
      this.paymentRequest.update({
        total: {
          label: 'Total price',
          amount: this.getPaymentAmount(),
        },
      });
    }
  }

  async ngOnInit() {
    this.loading = await this.loadingController.create({
      mode: 'md',
      message: 'Loading payment form...',
    });
    await this.loading.present();
    try {
      const stripe = await this.stripe;
      this.elements = stripe.elements();
      const styleTheme: Stripe.StripeElementStyle = {
        base: {
          fontFamily: '"Lucida Console" "sans-serif" "arial"',
        },
      };

      const productIdFromRoute =
        this.router.routerState.snapshot.root.queryParamMap.get('productId');
      if (productIdFromRoute) {
        this.appService.chosenProductId = productIdFromRoute;
      }

      if (!this.appService.priceInformation) {
        await this.appService.initSelectedPrice(true);
      }

      if (
        !this.paymentIntegratedEmail &&
        this.appService.priceInformation.unitAmount > 0
      ) {
        this.paymentRequest = stripe.paymentRequest({
          country: 'AU',
          currency: 'aud',
          total: {
            label: 'Total price',
            amount: this.getPaymentAmount(),
          },
          requestPayerName: false,
          requestPayerEmail: false,
          requestPayerPhone: false,
        });
        this.paymentRequestAvailable =
          !!(await this.paymentRequest.canMakePayment());
        if (this.paymentRequestAvailable) {
          // Handle payment method selection from Link/Apple Pay/Google Pay
          this.paymentRequest.on('paymentmethod', async (ev) => {
            try {
              this.loading = await this.loadingController.create({
                mode: 'md',
                message: 'Please wait while your payment processes...',
              });
              await this.loading.present();

              const attributes = await Auth.userAttributes(
                this.appService.user
              );
              const userEmail = attributes.find(
                (attribute) => attribute.Name === 'email'
              ).Value;

              const couponCode =
                this.couponInput?.value?.toString()?.trim() || null;

              // Process payment through backend API to ensure coupon is applied
              const paymentResult = await this.appService
                .stripeAPICall('POST', 'payment', {
                  userInformation: {
                    address: null,
                  },
                  paymentMethodId: ev.paymentMethod.id,
                  productId:
                    this.appService.chosenProductId ??
                    this.appService.productInformation.id,
                  couponCode,
                })
                .catch((error: RequestError) => {
                  ev.complete('fail');
                  throw Error(error.response.data.error);
                });

              ev.complete('success');
              this.router.navigate(['/success'], {
                queryParams: { subId: paymentResult.subId },
              });
            } catch (err) {
              console.error('ERROR:', err);
              this.errorDisplay = /Request failed/i.test(err.message)
                ? 'Sorry, something went wrong. Please try again later.'
                : err.message;
              ev.complete('fail');
            } finally {
              if (this.loading) {
                await this.loading.dismiss();
              }
            }
          });

          const elementPayment = this.elements.create('paymentRequestButton', {
            paymentRequest: this.paymentRequest,
          });
          elementPayment.mount('#payment-request-button');
        }
      }

      const elementCardNumber = this.elements.create('cardNumber', {
        style: styleTheme,
        showIcon: true,
      });
      const elementCardExpiry = this.elements.create('cardExpiry', {
        style: styleTheme,
      });
      const elementCVC = this.elements.create('cardCvc', {
        style: styleTheme,
        placeholder: '123',
      });

      elementCardNumber.on('change', (event) => {
        this.formReadyFields.card = event.complete;
        this.verifyForm();
      });
      elementCardExpiry.on('change', (event) => {
        this.formReadyFields.expiry = event.complete;
        this.verifyForm();
      });
      elementCVC.on('change', (event) => {
        this.formReadyFields.cvc = event.complete;
        this.verifyForm();
      });

      elementCardNumber.mount('#card-number-element');
      elementCardExpiry.mount('#card-expiry-element');
      elementCVC.mount('#card-cvc-element');
    } catch (error) {
      console.error('Error initializing Stripe Elements:', error);
      this.errorDisplay =
        'Failed to load payment form. Please refresh the page or check your internet connection.';
    } finally {
      this.loading.dismiss();
    }
  }

  ngOnDestroy(): void {
    if (this.couponValidationTimeout) {
      clearTimeout(this.couponValidationTimeout);
    }
  }

  async processPayment() {
    this.errorDisplay = null;
    this.loading = await this.loadingController.create({
      mode: 'md',
      message: 'Please wait while your payment processes...',
    });
    await this.loading.present();
    const attributes = await Auth.userAttributes(this.appService.user);
    const userEmail = attributes.find(
      (attribute) => attribute.Name === 'email'
    ).Value;
    try {
      if (!this.addressForm.line1 && this.includeAddress) {
        this.errorDisplay = 'Address Line 1 is required.';
        return;
      }

      const address = this.includeAddress
        ? ({
            line1: this.addressForm.line1,
            line2: this.addressForm.line2,
            country:
              this.COUNTRY_MAPPINGS[this.addressForm.country] ??
              this.addressForm.country,
            state: this.addressForm.state,
            city: this.addressForm.city,
            postal_code: this.addressForm.postal,
          } as StripeAddress)
        : null;

      const paymentMethodResult = await this.createPaymentMethod(
        userEmail,
        address
      );

      const couponCode = this.couponInput?.value?.toString()?.trim() || null;

      const paymentResult = await this.appService
        .stripeAPICall('POST', 'payment', {
          userInformation: {
            address,
          },
          paymentMethodId: paymentMethodResult.paymentMethod.id,
          productId:
            this.appService.chosenProductId ??
            this.appService.productInformation.id,
          couponCode,
        })
        .catch((error: RequestError) => {
          throw Error(error.response.data.error);
        });

      this.router.navigate(['/success'], {
        queryParams: { subId: paymentResult.subId },
      });
      return;
    } catch (err) {
      console.error('ERROR:', err);
      this.errorDisplay = /Request failed/i.test(err.message)
        ? 'Sorry, something went wrong. Please try again later.'
        : err.message;
    } finally {
      this.loading.dismiss();
    }
  }

  async createPaymentMethod(email: string, address?: StripeAddress) {
    return await this.stripe
      .then((stripe) =>
        stripe.createPaymentMethod({
          card: this.elements.getElement('cardNumber'),
          billing_details: {
            name: this.cardNameElement.value,
            email,
            address,
          },
          type: 'card',
        } as Stripe.CreatePaymentMethodData)
      )
      .catch((error) => {
        throw Error(error.message);
      });
  }

  permissionChange() {
    this.customerPermission = !this.customerPermission;
    this.permissionCheckbox.checked = this.customerPermission;
    this.verifyForm();
  }

  nameChange() {
    this.formReadyFields.name = !!this.cardNameElement.value;
    this.verifyForm();
  }

  verifyForm() {
    this.formReady =
      this.formReadyFields.card &&
      this.formReadyFields.expiry &&
      this.formReadyFields.cvc &&
      this.formReadyFields.name &&
      this.customerPermission;
  }

  onCouponInput(): void {
    if (!this.couponInput) {
      return;
    }

    // transform input to uppercase and replace spaces with dashes
    const currentValue = this.couponInput.value?.toString() || '';
    const transformedValue = currentValue.toUpperCase().replace(/\s+/g, '-');

    if (currentValue !== transformedValue) {
      this.couponInput.value = transformedValue;
    }

    if (this.couponValidationTimeout) {
      clearTimeout(this.couponValidationTimeout);
    }

    this.couponError = null;

    this.couponValidationTimeout = setTimeout(async () => {
      const couponCode = transformedValue.trim();

      if (!couponCode) {
        if (this.appService.priceInformation) {
          this.appService.priceInformation.couponUnitAmount = null;
          this.appService.priceInformation.discount = null;
        }
        this.updatePaymentRequest();
        return;
      }

      if (couponCode.length < 3) {
        return;
      }

      try {
        this.isCouponValidating = true;
        await this.appService.getCouponInfo(couponCode);
        this.couponError = null;
        this.updatePaymentRequest();
      } catch (error) {
        this.couponError =
          error?.message || 'Invalid coupon code. Please check and try again.';
        if (this.appService.priceInformation) {
          this.appService.priceInformation.couponUnitAmount = null;
          this.appService.priceInformation.discount = null;
        }
        this.updatePaymentRequest();
      } finally {
        this.isCouponValidating = false;
      }
    }, 400);
  }
}
